/**
 * Calculates optimal parameters based on workflow task count
 * Optimized based on SFLAGA Parameter Correlations Report
 * 
 * Key correlations applied:
 * 1. Population Size: Scales with problem size (base + scaling factor)
 * 2. Memeplex Count = Population Size / 20 (critical 20:1 ratio)
 * 3. Crossover Rate: Increases with problem size (75-85%)
 * 4. Mutation Rate: Decreases with problem size, maintains 5-7:1 ratio with crossover
 * 5. Iterations: Scales proportionally to population size
 * 
 * Formulas based on optimal correlations:
 * - Small (≤20): pop = 180 + taskCount, crossover = 75, mutation = 15
 * - Medium (21-50): pop = 200 + taskCount, crossover = 75 + (taskCount-21)/29*5, mutation = 15
 * - Large (51-100): pop = 300 + taskCount, crossover = 80-85, mutation = 12-10
 * - Very Large (101-500): pop = 400 + taskCount*0.2, crossover = 85, mutation = 10
 * - Extremely Large (501-1000): pop = 650 + taskCount*0.1, crossover = 85, mutation = 10
 * 
 * See docs/SFLAGA_PARAMETER_CORRELATIONS_REPORT.md for detailed analysis
 */
export function getOptimalParametersForTaskCount(taskCount: number) {
  let initialPopulation: number;
  let crossoverRate: number;
  let mutationRate: number;
  let iterations: number;
  
  // Calculate population size based on problem size (scaling formula)
  if (taskCount <= 20) {
    // Small Problems: pop = 180 + taskCount (scales from 181 to 200)
    initialPopulation = Math.round(180 + taskCount);
    crossoverRate = 75;
    mutationRate = 15;
  } else if (taskCount <= 50) {
    // Medium Problems: pop = 200 + taskCount (scales from 221 to 250)
    // This is the optimal range for SFLAGA
    initialPopulation = Math.round(200 + taskCount);
    // Crossover scales from 75% to 80% as problem size increases
    crossoverRate = Math.round(75 + ((taskCount - 21) / 29) * 5);
    mutationRate = 15;
  } else if (taskCount <= 100) {
    // Large Problems: pop = 250 + taskCount (scales from 301 to 350)
    initialPopulation = Math.round(250 + taskCount);
    // Crossover scales from 80% to 85%
    crossoverRate = Math.round(80 + ((taskCount - 51) / 49) * 5);
    // Mutation scales from 15% to 12% (maintains 5-7:1 ratio with crossover)
    mutationRate = Math.round(15 - ((taskCount - 51) / 49) * 3);
  } else if (taskCount <= 500) {
    // Very Large Problems: pop = 400 + taskCount*0.2 (scales from 420 to 500)
    initialPopulation = Math.round(400 + taskCount * 0.2);
    crossoverRate = 85;
    mutationRate = 10;
  } else {
    // Extremely Large Problems: pop = 650 + taskCount*0.1 (scales from 700 to 750, capped at 700)
    initialPopulation = Math.min(700, Math.round(650 + taskCount * 0.1));
    crossoverRate = 85;
    mutationRate = 10;
  }
  
  // Ensure population is within reasonable bounds
  initialPopulation = Math.max(150, Math.min(700, initialPopulation));
  
  // Memeplex Count = Population Size / 20 (critical 20:1 ratio correlation)
  const memeplexCount = Math.max(4, Math.min(35, Math.round(initialPopulation / 20)));
  
  // Iterations scale proportionally with population size
  // Formula: iterations ≈ populationSize (for medium problems)
  // For small: iterations ≈ 0.9 * populationSize
  // For large: iterations ≈ 0.85 * populationSize
  if (taskCount <= 20) {
    iterations = Math.round(initialPopulation * 0.9);
  } else if (taskCount <= 50) {
    iterations = Math.round(initialPopulation * 1.0); // Optimal: iterations = population
  } else if (taskCount <= 100) {
    iterations = Math.round(initialPopulation * 0.85);
  } else if (taskCount <= 500) {
    iterations = Math.round(initialPopulation * 0.8);
  } else {
    iterations = Math.round(initialPopulation * 0.71);
  }
  
  // Ensure iterations are within reasonable bounds
  iterations = Math.max(100, Math.min(500, iterations));
  
  // Ensure crossover and mutation are within bounds
  crossoverRate = Math.max(75, Math.min(85, crossoverRate));
  mutationRate = Math.max(10, Math.min(18, mutationRate));
  
  return {
    iterations,
    initialPopulation,
    crossoverRate,
    mutationRate,
    memeplexCount,
  };
}

